import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import zIndex from 'styles/tools/z-index'
import useBedbankPackageViews from 'hooks/useBedbankPackageViews'
import BedbankAboutRoomModalTab from './BedbankAboutRoomModalTab'
import BedbankPackageOptionsModalTab from './BedbankPackageOptionsModalTab'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { sortBy } from 'lib/array/arrayUtils'
import { GOOGLE_ADS_UTM_CONTENT } from 'constants/bedbank'
import useQueryParams from 'hooks/useQueryParams'
import BedbankMobileStickyHeaderFilters from '../BedbankMobileStickyHeaderFilters'
import { getCanUseMultiRoomUI } from 'selectors/offerPage/bedbankOfferPageSelectors'

const StickyTabButtonsGroup = styled(TabButtonsGroup)`
  position: sticky;
  top: 0;
  z-index: ${zIndex.stickyHeader};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

interface Props {
  offer: App.BedbankOffer
  selectedBedbankRoomId: string
  onClose: () => void
  canUseMultiRoomUI: boolean
}

function BedbankRoomAndPackageDetailsModal(props: Props) {
  const {
    offer,
    selectedBedbankRoomId,
    onClose,
    canUseMultiRoomUI,
  } = props

  const query = useQueryParams()
  const roomTypeId = query.get('roomTypeId')
  const utmContent = query.get('utm_content')
  const [selectedTab, setSelectedTab] = useState<'package-options' | 'about-room'>('package-options')

  // Can't view/select package options here if we're in multi-room mode
  const currentTab = canUseMultiRoomUI ? 'about-room' : selectedTab

  const [packageViews, fetchingRates] = useBedbankPackageViews(offer)
  const selectedPackageView = useMemo(() => packageViews.find(view => view.package.roomId === selectedBedbankRoomId), [packageViews, selectedBedbankRoomId])

  const showAll = packageViews.filter(p => p.available).length === 0
  const sortedPackageViews = useMemo(() => {
    return sortBy(packageViews, pkg => {
      if (pkg.cheapestRate) {
        return pkg.cheapestRate.totals.inclusive + (pkg.cheapestRate.totals.propertyFees ?? 0)
      }

      if (!pkg.capacityValid) {
        return Number.MAX_SAFE_INTEGER - 1
      }

      return Number.MAX_SAFE_INTEGER
    }, 'asc')
  }, [packageViews])

  const sortedPackages = useMemo(() => {
    const highlightedPackage = sortedPackageViews.find(p => p.package.roomId === roomTypeId)
    if (utmContent === GOOGLE_ADS_UTM_CONTENT && highlightedPackage) {
      return [
        highlightedPackage,
        sortedPackageViews.find(p => p.package.roomId !== roomTypeId),
      ].filter((p): p is App.BedbankOfferPackageView => !!p) // this fix type inference
    }
    else {
      return sortedPackageViews
    }
  }, [sortedPackageViews, utmContent, roomTypeId])

  const displayablePackages = useMemo(() => sortedPackages.filter(
    packageView => packageView.show && (packageView.available || showAll),
  ), [sortedPackages, showAll])

  const roomPosition = useMemo(() => displayablePackages.findIndex(p => p.package.roomId === selectedPackageView?.package.roomId), [displayablePackages, selectedPackageView])

  const selectPackageOptionsTab = useCallback(() => {
    setSelectedTab('package-options')
  }, [])

  const selectAboutRoomTab = useCallback(() => {
    setSelectedTab('about-room')
  }, [])

  if (!selectedPackageView) return null

  return (<>
    <ModalHeader
      title={selectedPackageView.package.name}
      onCloseButtonClick={onClose}
    >
      <BedbankMobileStickyHeaderFilters />
    </ModalHeader>
    <ModalBody>
      <LoadingIndicator visible={fetchingRates} floating opaque />
      <AccommodationRoomTile roomPackage={selectedPackageView.package}>
        <AccommodationRoomTileImage roomPackage={selectedPackageView.package} />
      </AccommodationRoomTile>
      {!canUseMultiRoomUI && <StickyTabButtonsGroup fit="grow">
        <TabButton size="medium" isActive={currentTab === 'package-options'} onClick={selectPackageOptionsTab}>
          Package options
        </TabButton>
        <TabButton size="medium" isActive={currentTab === 'about-room'} onClick={selectAboutRoomTab}>
          About the room
        </TabButton>
      </StickyTabButtonsGroup>}
      {currentTab === 'package-options' && <BedbankPackageOptionsModalTab packageView={selectedPackageView} offer={offer} roomPosition={roomPosition} />}
      {currentTab === 'about-room' && <BedbankAboutRoomModalTab roomPackage={selectedPackageView.package} />}
    </ModalBody>
  </>)
}

export default withOfferPageAndReduxState((state, appState) => ({
  offer: state.bedbankOffer!,
  selectedBedbankRoomId: state.selectedBedbankRoomId,
  canUseMultiRoomUI: getCanUseMultiRoomUI(state, appState),
}), BedbankRoomAndPackageDetailsModal)
